import * as React from "react"
import * as TabsPrimitive from "@radix-ui/react-tabs"

import { cn } from "@/lib/utils"

/**
 * `tarjetas` — cada pestaña es una tarjeta con su propio estado, agrupadas en
 * bandas. Nació para el editor de informes, donde los rótulos con línea
 * inferior no entraban: doce pestañas en una sola fila obligan a scrollear
 * horizontalmente, y en los equipos de Consultek (viewport de ~800 px) la mitad
 * quedaba fuera de pantalla sin que nada indicara que estaba ahí.
 *
 * La variante pone la CAJA —grilla, radios, borde, activo y hover—; el
 * contenido de cada tarjeta lo arma quien la usa, porque el punto de color y el
 * contador son del dominio y no del Design System.
 */
type TabsVariant = "default" | "underline" | "underline-ink" | "tarjetas"

const TabsContext = React.createContext<{ variant: TabsVariant }>({
  variant: "default",
})

function Tabs({
  className,
  variant = "default",
  ...props
}: React.ComponentProps<typeof TabsPrimitive.Root> & {
  variant?: TabsVariant
}) {
  return (
    <TabsContext.Provider value={{ variant }}>
      <TabsPrimitive.Root
        data-slot="tabs"
        data-variant={variant}
        className={cn("flex flex-col gap-2", className)}
        {...props}
      />
    </TabsContext.Provider>
  )
}

function TabsList({
  className,
  ...props
}: React.ComponentProps<typeof TabsPrimitive.List>) {
  const { variant } = React.useContext(TabsContext)
  return (
    <TabsPrimitive.List
      data-slot="tabs-list"
      className={cn(
        variant === "tarjetas"
          ? /* Las BANDAS van adentro de esta misma lista y no en dos listas
               separadas: Radix mueve el foco con las flechas entre los triggers
               de UNA lista, así que partirla dejaría el teclado saltando solo
               dentro de cada banda. Los rótulos de grupo pueden vivir en el
               medio — la colección de Radix encuentra los triggers por
               referencia, sin importar cuánto DOM haya en el camino. */
            "flex w-full min-w-0 flex-col items-stretch gap-[0.5625rem]"
          : variant === "underline" || variant === "underline-ink"
            ? "text-muted-foreground inline-flex w-full items-center justify-start gap-6 border-b-[1.5px] border-border"
            : "bg-muted text-muted-foreground inline-flex h-9 w-fit items-center justify-center rounded-lg p-[0.1875rem]",
        className
      )}
      {...props}
    />
  )
}

function TabsTrigger({
  className,
  ...props
}: React.ComponentProps<typeof TabsPrimitive.Trigger>) {
  const { variant } = React.useContext(TabsContext)
  return (
    <TabsPrimitive.Trigger
      data-slot="tabs-trigger"
      className={cn(
        variant === "tarjetas"
          ? /* La tarjeta: alineada ARRIBA y no al centro, porque los rótulos de
               dos líneas conviven con los de una y el punto de color tiene que
               quedar a la altura de la primera línea en los dos casos.
               El borde va en px —1,5— porque una línea al escalar solo se
               vuelve borrosa; todo lo demás va en `rem` y acompaña la perilla
               de densidad de la aplicación.

               LAS MEDIDAS SON LAS DEL DISEÑO (Claude Design, "Editor de
               informe"), en px/16. Hubo una versión un 30 % más grande para
               que el rótulo no se leyera como nota al pie junto al formulario;
               se revirtió porque la barra ocupaba demasiado alto y se alejaba
               del diseño aprobado. */
            "border-border bg-superficie-sutil text-ink-muted hover:border-ink-ghost data-[state=active]:border-primary data-[state=active]:bg-card data-[state=active]:text-ink data-[state=active]:shadow-[0_4px_14px_-8px_rgba(20,22,26,.22)] focus-visible:border-ring focus-visible:ring-ring/50 flex min-w-0 items-start gap-[0.4375rem] rounded-[0.6875rem] border-[1.5px] px-[0.5625rem] pt-[0.4375rem] pb-[0.5rem] text-left text-[0.6875rem] font-semibold transition-colors outline-none focus-visible:ring-[3px] disabled:pointer-events-none disabled:opacity-50 data-[state=active]:font-bold"
          : variant === "underline-ink"
            ? /* El indicador NEGRO de 2 px de las pantallas con diseño (fases del
                 informe): el texto activo y la línea van en `ink`, y el contador
                 mono lo pone quien la usa. `underline` conserva el verde del DS. */
              "text-ink-medio data-[state=active]:text-ink data-[state=active]:border-ink data-[state=active]:font-bold -mb-[1.5px] inline-flex items-center gap-[0.4375rem] border-b-2 border-transparent px-0.5 pt-2 pb-2.5 text-[0.8125rem] font-semibold whitespace-nowrap outline-none transition-colors focus-visible:text-ink disabled:pointer-events-none disabled:opacity-50"
            : variant === "underline"
            ? "text-muted-foreground data-[state=active]:text-foreground data-[state=active]:border-primary data-[state=active]:font-bold -mb-[1.5px] inline-flex items-center gap-1.5 border-b-[2.5px] border-transparent px-0.5 pb-3 text-sm font-semibold whitespace-nowrap outline-none transition-colors focus-visible:text-foreground disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4"
            : "data-[state=active]:bg-background dark:data-[state=active]:text-foreground focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:outline-ring dark:data-[state=active]:border-input dark:data-[state=active]:bg-input/30 text-foreground dark:text-muted-foreground inline-flex h-[calc(100%-1px)] flex-1 items-center justify-center gap-1.5 rounded-md border border-transparent px-2 py-1 text-sm font-medium whitespace-nowrap transition-[color,box-shadow] focus-visible:ring-[3px] focus-visible:outline-1 disabled:pointer-events-none disabled:opacity-50 data-[state=active]:shadow-sm [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4",
        className
      )}
      {...props}
    />
  )
}

function TabsContent({
  className,
  ...props
}: React.ComponentProps<typeof TabsPrimitive.Content>) {
  return (
    <TabsPrimitive.Content
      data-slot="tabs-content"
      className={cn("flex-1 outline-none", className)}
      {...props}
    />
  )
}

export { Tabs, TabsList, TabsTrigger, TabsContent }
